import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { RootState } from 'store'
import {
  getCourse,
  deactivateCourse,
  reactivateCourse,
  editCourse,
  createCourseVersion,
  createCourse,
  setCourseVersionOfficial,
  setCourseVersionUnofficial,
  duplicateCourse,
  transferCourse,
  transferCourseProviderMapping,
  doCourseTeeBoxMapping,
  generateInitialMapping,
  resetCourseMapping,
  setCourseGpsVersionOfficial,
  setCourseGpsVersionUnofficial,
  createCourseGpsVersion,
  flipCourseGpsVersionMapping,
  copyCourseGpsMapping,
  editCourseVersion,
  createCourseGpsVersionZendeskTicket,
  createCourseVersionZendeskTicket,
  setCourseVersionReviewed,
  setCourseGpsVersionReviewed,
  initCourseHoleStats,
  getCourseHoleStatsResults,
  cancelCourseHoleStats,
  checkCourseHoleStatsStatus,
  updateCourseProviderVersion,
  updateCourseProviderVersionMap,
  updateCourseProviderMapping,
  getProviderCourses,
} from './thunks'
import { GPS_EDITOR_TUTORIAL, saveGpsEditorTutorialPhase } from './utils'

const course: Course = {
  id: 0,
  name: '',
}

const initialState: CourseState = {
  course: course,
  courseHoleStatsStatusResponse: null,
  initCourseHoleStatsResponse: null,
  duplicatedCourse: null,
  mappingTransferTargetCourse: null,
  gpsEditorTutorialPhase: GPS_EDITOR_TUTORIAL.Start,
  status: 'idle',
  error: null,
  saveStatus: 'idle',
  saveError: null,
  versionSaveStatus: 'idle',
  versionSaveError: null,
  versionZendeskStatus: 'idle',
  versionZendeskError: null,
  versionOfficialSaveStatus: 'idle',
  versionOfficialSaveError: null,
  transferMappingStatus: 'idle',
  transferMappingError: null,
  initialMappingStatus: 'idle',
  initialMappingError: null,
  teeBoxMappingStatus: 'idle',
  teeBoxMappingError: null,
  resetMappingStatus: 'idle',
  resetMappingError: null,
  gpsVersionOfficialStatus: 'idle',
  gpsVersionOfficialError: null,
  gpsVersionStatus: 'idle',
  gpsVersionError: null,
  gpsVersionZendeskStatus: 'idle',
  gpsVersionZendeskError: null,
  flipGpsVersionMappingStatus: 'idle',
  flipGpsVersionMappingError: null,
  copyCourseGpsMappingStatus: 'idle',
  copyCourseGpsMappingError: null,
  initCourseHoleStatsStatus: 'idle',
  initCourseHoleStatsError: null,
  getCourseHoleStatsStatus: 'idle',
  getCourseHoleStatsError: null,
  cancelCourseHoleStatsStatus: 'idle',
  cancelCourseHoleStatsError: null,
  getCourseHoleStatsResultsStatus: 'idle',
  getCourseHoleStatsResultsError: null,
  updateCourseProviderVersionStatus: 'idle',
  updateCourseProviderVersionError: null,
  updateCourseProviderVersionMapStatus: 'idle',
  updateCourseProviderVersionMapError: null,
  updateCourseProviderMappingStatus: 'idle',
  updateCourseProviderMappingError: null,
  getProviderCoursesStatus: 'idle',
  getProviderCoursesError: null,
}

const slice = createSlice({
  name: 'course',
  initialState,
  reducers: {
    clearCourse: () => initialState,
    clearTeeBoxMapping: (state) => {
      state.teeBoxMappingError = null
      state.teeBoxMappingStatus = 'idle'
    },
    clearDuplicatedCourse: (state) => {
      state.duplicatedCourse = null
    },
    clearMappingTransferTargetCourse: (state) => {
      state.mappingTransferTargetCourse = null
    },
    clearGpsMappingStatus: (state) => {
      state.flipGpsVersionMappingStatus = 'idle'
      state.flipGpsVersionMappingError = null
      state.copyCourseGpsMappingStatus = 'idle'
      state.copyCourseGpsMappingError = null
    },
    clearGpsVersionStatus: (state) => {
      state.gpsVersionStatus = 'idle'
      state.gpsVersionError = null
      state.gpsVersionOfficialStatus = 'idle'
      state.gpsVersionOfficialError = null
      state.gpsVersionZendeskStatus = 'idle'
      state.gpsVersionZendeskError = null
    },
    clearVersionStatus: (state) => {
      state.versionSaveStatus = 'idle'
      state.versionSaveError = null
      state.versionZendeskStatus = 'idle'
      state.versionZendeskError = null
    },
    setGpsEditorTutorialPhase: (state, action: PayloadAction<GpsEditorTutorialPhase | null>) => {
      state.gpsEditorTutorialPhase = action.payload
      saveGpsEditorTutorialPhase(action.payload)
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCourse.pending, (state) => {
        state.status = 'loading'
        state.error = null
        state.course = course
      })
      .addCase(getCourse.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(getCourse.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.course = { ...state.course, ...action.payload }
      })

    builder
      .addCase(editCourse.pending, (state) => {
        state.saveStatus = 'loading'
        state.saveError = null
      })
      .addCase(editCourse.rejected, (state, action) => {
        state.saveStatus = 'failed'
        state.saveError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(editCourse.fulfilled, (state, action) => {
        state.saveStatus = 'succeeded'
        state.course = action.payload
      })

    builder
      .addCase(createCourse.pending, (state) => {
        state.saveStatus = 'loading'
        state.saveError = null
      })
      .addCase(createCourse.rejected, (state, action) => {
        state.saveStatus = 'failed'
        state.saveError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(createCourse.fulfilled, (state, action) => {
        state.saveStatus = 'succeeded'
        state.course = action.payload
      })

    builder
      .addCase(duplicateCourse.pending, (state) => {
        state.saveStatus = 'loading'
        state.saveError = null
      })
      .addCase(duplicateCourse.rejected, (state, action) => {
        state.saveStatus = 'failed'
        state.saveError = action?.payload?.message || 'Something went wrong . Please try again.'
      })
      .addCase(duplicateCourse.fulfilled, (state, action) => {
        state.saveStatus = 'succeeded'
        state.duplicatedCourse = action.payload
      })

    builder
      .addCase(transferCourse.pending, (state) => {
        state.saveStatus = 'loading'
        state.saveError = null
      })
      .addCase(transferCourse.rejected, (state, action) => {
        state.saveStatus = 'failed'
        state.saveError = action?.payload?.message || 'Something went wrong . Please try again.'
      })
      .addCase(transferCourse.fulfilled, (state) => {
        state.saveStatus = 'succeeded'
      })

    builder
      .addCase(createCourseVersion.pending, (state) => {
        state.versionSaveStatus = 'loading'
        state.versionSaveError = null
      })
      .addCase(createCourseVersion.rejected, (state, action) => {
        state.versionSaveStatus = 'failed'
        state.versionSaveError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(createCourseVersion.fulfilled, (state, action) => {
        state.versionSaveStatus = 'succeeded'
        state.course.versions?.push(action.payload)
      })

    builder
      .addCase(deactivateCourse.pending, (state) => {
        state.saveStatus = 'loading'
        state.saveError = null
      })
      .addCase(deactivateCourse.rejected, (state, action) => {
        state.saveStatus = 'failed'
        state.saveError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(deactivateCourse.fulfilled, (state) => {
        state.saveStatus = 'succeeded'
      })

    builder
      .addCase(reactivateCourse.pending, (state) => {
        state.saveStatus = 'loading'
        state.saveError = null
      })
      .addCase(reactivateCourse.rejected, (state, action) => {
        state.saveStatus = 'failed'
        state.saveError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(reactivateCourse.fulfilled, (state) => {
        state.saveStatus = 'succeeded'
      })

    builder
      .addCase(transferCourseProviderMapping.pending, (state) => {
        state.transferMappingStatus = 'loading'
        state.transferMappingError = null
      })
      .addCase(transferCourseProviderMapping.rejected, (state, action) => {
        state.transferMappingStatus = 'failed'
        state.transferMappingError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(transferCourseProviderMapping.fulfilled, (state, action) => {
        state.transferMappingStatus = 'succeeded'
        state.course = action.payload.fromCourse
        state.mappingTransferTargetCourse = action.payload.toCourse
      })

    builder
      .addCase(doCourseTeeBoxMapping.pending, (state) => {
        state.teeBoxMappingStatus = 'loading'
        state.teeBoxMappingError = null
      })
      .addCase(doCourseTeeBoxMapping.rejected, (state, action) => {
        state.teeBoxMappingStatus = 'failed'
        state.teeBoxMappingError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(doCourseTeeBoxMapping.fulfilled, (state, action) => {
        state.teeBoxMappingStatus = 'succeeded'
        state.versionOfficialSaveStatus = 'idle'
        state.course = action.payload
      })

    builder
      .addCase(setCourseVersionOfficial.pending, (state) => {
        state.versionOfficialSaveStatus = 'loading'
        state.versionOfficialSaveError = null
      })
      .addCase(setCourseVersionOfficial.rejected, (state, action) => {
        state.versionOfficialSaveStatus = 'failed'
        state.versionOfficialSaveError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(setCourseVersionOfficial.fulfilled, (state, action) => {
        state.versionOfficialSaveStatus = 'succeeded'
        const versionId = action.meta.arg
        const versionIsFromCurrentCourse = state.course.versions?.some((cv: CourseVersion) => cv.id === versionId)
        if (versionIsFromCurrentCourse) {
          const versions = syncCourseVersions(action.payload, state.course.versions)
          state.course.versions = versions
        }
      })

    builder
      .addCase(setCourseVersionUnofficial.pending, (state) => {
        state.versionOfficialSaveStatus = 'loading'
        state.versionOfficialSaveError = null
      })
      .addCase(setCourseVersionUnofficial.rejected, (state, action) => {
        state.versionOfficialSaveStatus = 'failed'
        state.versionOfficialSaveError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(setCourseVersionUnofficial.fulfilled, (state, action) => {
        state.versionOfficialSaveStatus = 'succeeded'
        const versionId = action.meta.arg
        const versionIsFromCurrentCourse = state.course.versions?.some((cv: CourseVersion) => cv.id === versionId)
        if (versionIsFromCurrentCourse) {
          const versions = syncCourseVersions(undefined, state.course.versions)
          state.course.versions = versions
        }
      })

    builder
      .addCase(setCourseVersionReviewed.pending, (state) => {
        state.saveStatus = 'loading'
        state.saveError = null
      })
      .addCase(setCourseVersionReviewed.rejected, (state, action) => {
        state.saveStatus = 'failed'
        state.saveError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(setCourseVersionReviewed.fulfilled, (state, action) => {
        state.saveStatus = 'succeeded'
        state.course = action.payload
      })

    builder
      .addCase(generateInitialMapping.pending, (state) => {
        state.initialMappingStatus = 'loading'
        state.initialMappingError = null
      })
      .addCase(generateInitialMapping.rejected, (state, action) => {
        state.initialMappingStatus = 'failed'
        state.initialMappingError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(generateInitialMapping.fulfilled, (state, action) => {
        state.initialMappingStatus = 'succeeded'
        state.course = action.payload
      })

    builder
      .addCase(resetCourseMapping.pending, (state) => {
        state.resetMappingStatus = 'loading'
        state.resetMappingError = null
      })
      .addCase(resetCourseMapping.rejected, (state, action) => {
        state.resetMappingStatus = 'failed'
        state.resetMappingError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(resetCourseMapping.fulfilled, (state, action) => {
        state.resetMappingStatus = 'succeeded'
        state.course = action.payload
      })

    builder
      .addCase(setCourseGpsVersionOfficial.pending, (state) => {
        state.gpsVersionOfficialStatus = 'loading'
        state.gpsVersionOfficialError = null
      })
      .addCase(setCourseGpsVersionOfficial.rejected, (state, action) => {
        state.gpsVersionOfficialStatus = 'failed'
        state.gpsVersionOfficialError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(setCourseGpsVersionOfficial.fulfilled, (state, action) => {
        state.gpsVersionOfficialStatus = 'succeeded'
        state.course = action.payload
      })

    builder
      .addCase(setCourseGpsVersionUnofficial.pending, (state) => {
        state.gpsVersionOfficialStatus = 'loading'
        state.gpsVersionOfficialError = null
      })
      .addCase(setCourseGpsVersionUnofficial.rejected, (state, action) => {
        state.gpsVersionOfficialStatus = 'failed'
        state.gpsVersionOfficialError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(setCourseGpsVersionUnofficial.fulfilled, (state, action) => {
        state.gpsVersionOfficialStatus = 'succeeded'
        state.course = action.payload
      })

    builder
      .addCase(setCourseGpsVersionReviewed.pending, (state) => {
        state.saveStatus = 'loading'
        state.saveError = null
      })
      .addCase(setCourseGpsVersionReviewed.rejected, (state, action) => {
        state.saveStatus = 'failed'
        state.saveError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(setCourseGpsVersionReviewed.fulfilled, (state, action) => {
        state.saveStatus = 'succeeded'
        state.course = action.payload
      })

    builder
      .addCase(createCourseGpsVersion.pending, (state) => {
        state.gpsVersionStatus = 'loading'
        state.gpsVersionError = null
      })
      .addCase(createCourseGpsVersion.rejected, (state, action) => {
        state.gpsVersionStatus = 'failed'
        state.gpsVersionError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(createCourseGpsVersion.fulfilled, (state, action) => {
        state.gpsVersionStatus = 'succeeded'
        state.course = action.payload
      })

    builder
      .addCase(createCourseGpsVersionZendeskTicket.pending, (state) => {
        state.gpsVersionZendeskStatus = 'loading'
        state.gpsVersionZendeskError = null
      })
      .addCase(createCourseGpsVersionZendeskTicket.rejected, (state, action) => {
        if (action.meta.arg.retryOnFailure === false) {
          state.gpsVersionZendeskStatus = 'failed'
          state.gpsVersionZendeskError = action?.payload?.message || 'Something went wrong. Please try again.'
        }
      })
      .addCase(createCourseGpsVersionZendeskTicket.fulfilled, (state) => {
        state.gpsVersionZendeskStatus = 'succeeded'
      })

    builder
      .addCase(createCourseVersionZendeskTicket.pending, (state) => {
        state.versionZendeskStatus = 'loading'
        state.versionZendeskError = null
      })
      .addCase(createCourseVersionZendeskTicket.rejected, (state, action) => {
        if (action.meta.arg.retryOnFailure === false) {
          state.versionZendeskStatus = 'failed'
          state.versionZendeskError = action?.payload?.message || 'Something went wrong. Please try again.'
        }
      })
      .addCase(createCourseVersionZendeskTicket.fulfilled, (state) => {
        state.versionZendeskStatus = 'succeeded'
      })

    builder
      .addCase(flipCourseGpsVersionMapping.pending, (state) => {
        state.flipGpsVersionMappingStatus = 'loading'
        state.flipGpsVersionMappingError = null
      })
      .addCase(flipCourseGpsVersionMapping.rejected, (state, action) => {
        state.flipGpsVersionMappingStatus = 'failed'
        state.flipGpsVersionMappingError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(flipCourseGpsVersionMapping.fulfilled, (state, action) => {
        state.flipGpsVersionMappingStatus = 'succeeded'
        state.course = action.payload
      })

    builder
      .addCase(copyCourseGpsMapping.pending, (state) => {
        state.copyCourseGpsMappingStatus = 'loading'
        state.copyCourseGpsMappingError = null
      })
      .addCase(copyCourseGpsMapping.rejected, (state, action) => {
        state.copyCourseGpsMappingStatus = 'failed'
        state.copyCourseGpsMappingError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(copyCourseGpsMapping.fulfilled, (state, action) => {
        state.copyCourseGpsMappingStatus = 'succeeded'
        state.course = action.payload
      })

    builder
      .addCase(editCourseVersion.pending, (state) => {
        state.versionSaveStatus = 'loading'
        state.versionSaveError = null
      })
      .addCase(editCourseVersion.rejected, (state, action) => {
        state.versionSaveStatus = 'failed'
        state.versionSaveError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(editCourseVersion.fulfilled, (state, action) => {
        state.versionSaveStatus = 'succeeded'
        state.course = action.payload
      })

    builder
      .addCase(initCourseHoleStats.pending, (state) => {
        state.initCourseHoleStatsStatus = 'loading'
        state.initCourseHoleStatsError = null
      })
      .addCase(initCourseHoleStats.rejected, (state, action) => {
        state.initCourseHoleStatsStatus = 'failed'
        state.initCourseHoleStatsError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(initCourseHoleStats.fulfilled, (state, action) => {
        state.initCourseHoleStatsStatus = 'succeeded'
        state.initCourseHoleStatsResponse = action.payload
      })

    builder
      .addCase(checkCourseHoleStatsStatus.pending, (state) => {
        state.getCourseHoleStatsStatus = 'loading'
        state.getCourseHoleStatsError = null
      })
      .addCase(checkCourseHoleStatsStatus.rejected, (state, action) => {
        state.getCourseHoleStatsStatus = 'failed'
        state.getCourseHoleStatsError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(checkCourseHoleStatsStatus.fulfilled, (state, action) => {
        state.getCourseHoleStatsStatus = 'succeeded'
        state.courseHoleStatsStatusResponse = action.payload
      })

    builder
      .addCase(cancelCourseHoleStats.pending, (state) => {
        state.cancelCourseHoleStatsStatus = 'loading'
        state.cancelCourseHoleStatsError = null
      })
      .addCase(cancelCourseHoleStats.rejected, (state, action) => {
        state.cancelCourseHoleStatsStatus = 'failed'
        state.cancelCourseHoleStatsError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(cancelCourseHoleStats.fulfilled, (state) => {
        state.cancelCourseHoleStatsStatus = 'succeeded'
      })

    builder
      .addCase(getCourseHoleStatsResults.pending, (state) => {
        state.getCourseHoleStatsResultsStatus = 'loading'
        state.getCourseHoleStatsResultsError = null
      })
      .addCase(getCourseHoleStatsResults.rejected, (state, action) => {
        state.getCourseHoleStatsResultsStatus = 'failed'
        state.getCourseHoleStatsResultsError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(getCourseHoleStatsResults.fulfilled, (state, action) => {
        state.getCourseHoleStatsResultsStatus = 'succeeded'
        state.course = action.payload
      })

    builder
      .addCase(updateCourseProviderVersion.pending, (state) => {
        state.updateCourseProviderVersionStatus = 'loading'
        state.updateCourseProviderVersionError = null
      })
      .addCase(updateCourseProviderVersion.rejected, (state, action) => {
        state.updateCourseProviderVersionStatus = 'failed'
        state.updateCourseProviderVersionError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(updateCourseProviderVersion.fulfilled, (state) => {
        state.updateCourseProviderVersionStatus = 'succeeded'
      })

    builder
      .addCase(updateCourseProviderVersionMap.pending, (state) => {
        state.updateCourseProviderVersionMapStatus = 'loading'
        state.updateCourseProviderVersionMapError = null
      })
      .addCase(updateCourseProviderVersionMap.rejected, (state, action) => {
        state.updateCourseProviderVersionMapStatus = 'failed'
        state.updateCourseProviderVersionMapError =
          action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(updateCourseProviderVersionMap.fulfilled, (state) => {
        state.updateCourseProviderVersionMapStatus = 'succeeded'
      })

    builder
      .addCase(updateCourseProviderMapping.pending, (state) => {
        state.updateCourseProviderMappingStatus = 'loading'
        state.updateCourseProviderMappingError = null
      })
      .addCase(updateCourseProviderMapping.rejected, (state, action) => {
        state.updateCourseProviderMappingStatus = 'failed'
        state.updateCourseProviderMappingError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(updateCourseProviderMapping.fulfilled, (state, action) => {
        state.updateCourseProviderMappingStatus = 'succeeded'
        state.course = { ...state.course, ...action.payload }
      })
    builder
      .addCase(getProviderCourses.pending, (state) => {
        state.getProviderCoursesStatus = 'loading'
        state.getProviderCoursesError = null
      })
      .addCase(getProviderCourses.rejected, (state, action) => {
        state.getProviderCoursesStatus = 'failed'
        state.getProviderCoursesError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(getProviderCourses.fulfilled, (state) => {
        state.getProviderCoursesStatus = 'succeeded'
      })
  },
})

const syncCourseVersions = (officialVersion: CourseVersion | undefined, versions: CourseVersion[] | undefined) => {
  if (versions) {
    versions.map((cv: CourseVersion) => {
      if (cv.id === officialVersion?.id) {
        Object.assign(cv, officialVersion)
      } else {
        cv.activeEndTime = cv.isOfficial ? officialVersion?.activeStartTime : cv.activeEndTime
        cv.isOfficial = false
      }
      return cv
    })
    return versions
  }
  return []
}

export const {
  clearCourse,
  clearTeeBoxMapping,
  clearDuplicatedCourse,
  clearGpsMappingStatus,
  clearGpsVersionStatus,
  clearVersionStatus,
  clearMappingTransferTargetCourse,
  setGpsEditorTutorialPhase,
} = slice.actions

export const selectCourse = (state: RootState): CourseState => state.course

export default slice.reducer
